import { Component } from '@angular/core';
import { UserService } from '../../../../../../../shared/services/user.service';
import { PeopleService } from '../../../../../../../shared/services/people.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastService } from '../../../../../../../shared/services/toast.service';
import { saveAs } from 'file-saver';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { environment } from '../../../../../../../../environments/environment';
import { constants } from '../../../../../../../shared/constants/constants';
import { ConfigurationService } from '../../../../../../../shared/services/configuration.service';

@Component({
  selector: 'app-bulk-invite',
  standalone: true,
  imports: [
    ButtonModule,
    TableModule
  ],
  templateUrl: './bulk-invite.component.html',
  styleUrl: './bulk-invite.component.scss'
})
export class BulkInviteComponent {

  user: any;
  firmId: string | undefined;
  csvPaginatedData: any[] = [];
  csvOrignalData: any[] = [];
  duplicateCsvUsers: string[] = [];
  pagination = {
    numPerPage: 25,
    currentPage: 1,
    maxSize: 25
  };
  csvData: any[] = [];
  expectedHeaders = ['First Name', 'Last Name', 'Email', 'Phone Number', 'Default Rating'];
  file: File | null = null;
  title = environment.title;
  jobTypeHeaders: Array<string> = [];
  isPwcEnabled = false;
  isSingleJobTypeEnabledFirm = false;

  constructor(
    private ref: DynamicDialogRef,
    private userService: UserService,
    private peopleService: PeopleService,
    private toastService: ToastService,
    private configurationService: ConfigurationService
  ) {
   }

  ngOnInit(): void {
    this.user = this.userService.getSessionUser();
    this.firmId = this.user?.firm?._id;
    this.isPwcEnabled = this.configurationService.getPwcFlag();
    this.isSingleJobTypeEnabledFirm = this.user?.firm?.isSingleJobTypeEnabled;
    if(!this.isSingleJobTypeEnabledFirm){
      this.expectedHeaders = [...this.expectedHeaders, 'Job type 1']
    }
    if(this.isPwcEnabled){
      this.expectedHeaders = [...this.expectedHeaders, 'PWC Email', 'Standard Pay Rate', 'Foreign language Pay Rate', 'Ranking', 'GUID', 'Contractor Number', 'Secondary Contractor Number']
    }
  }

  uploadCSV(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.file = input.files[0];
      this.readFile(this.file);
    }
  }

  bulkInviteCandidateSubmit(): void {
    const users = this.csvOrignalData.filter(user => !this.duplicateCsvUsers.includes(user.email));
    const body = {
      firmId: this.firmId,
      users: users,
      isBulk: true
    };

    this.peopleService.bulkInviteCandidates(body).subscribe({
      next: (response) => {
        this.ref.close({
          isSuccess: true,
          users: response.map((user: any) => {
            const selectedUser = users.find((csvUser: any) => user.email.toLowerCase() === csvUser.email.toLowerCase());
            user = {...user, pwcEmail: selectedUser.pwcEmail, primaryPayRate: selectedUser.primaryPayRate, secondaryPayRate: selectedUser.secondaryPayRate, rating: selectedUser.rating, ranking: selectedUser.ranking, guid: selectedUser.guid, contractorNumber: selectedUser.contractorNumber, secondaryContractorNumber: selectedUser.secondaryContractorNumber};
            if(selectedUser && selectedUser.jobTypes && selectedUser.jobTypes.length){
              return {...user, jobTypes: selectedUser.jobTypes};
            }
            return user;
          })
        });
      },
      error: (error) => {
        this.toastService.showError(error?.data?.message ?? 'Error while requesting.');
        this.ref.close(null);
      }
    });
  }

  parseCSV(csvText: string): any[] {
    const lines = csvText.split(/\r?\n|\r/);
    const headers = this.parseCSVLine(lines[0]);

    if (!this.validateHeaders(headers)) {
      return [];
    }

    this.setupJobTypeHeaders(headers);
    let records = this.getResult(lines, headers);
    
    records = records.filter((record: any) => {
      return !Object.values(record).every((v): v is string => typeof v === "string" && v.trim() === "");
    });
    return this.processRecords(records);
  }

  private validateHeaders(headers: string[]): boolean {
    const staticHeaders = this.getRequiredHeaders();
    
    for (const header of staticHeaders) {
      if (!headers.includes(header)) {
        this.toastService.showError('Required headers are missing');
        return false;
      }
    }
    return true;
  }

  private getRequiredHeaders(): string[] {
    let staticHeaders = ["First Name", "Last Name", "Email", "Phone Number"];
    if (this.isPwcEnabled) {
      staticHeaders = [...staticHeaders, "PWC Email", "Standard Pay Rate", "GUID", "Contractor Number"];
    }
    return staticHeaders;
  }

  private setupJobTypeHeaders(headers: string[]): void {
    if (!this.user?.firm?.isSingleJobTypeEnabled) {
      this.jobTypeHeaders = headers.filter(header => 
        header.toLowerCase().startsWith("Job Type".toLowerCase())
      );
    }
  }

  private processRecords(records: any[]): any[] {
    this.csvOrignalData = [];
    
    for (const record of records) {
      if (!this.validateRecord(record)) {
        return [];
      }
      
      this.processValidRecord(record);
    }
    
    return this.csvOrignalData;
  }

  private validateRecord(record: any): boolean {
    return this.validateRequiredFields(record) &&
           this.validateEmailFormats(record) &&
           this.validateNumericFields(record) &&
           this.validateFieldRanges(record) &&
           this.validateRanking(record);
  }

  private validateRequiredFields(record: any): boolean {
    if (!record["First Name"] || !record["Last Name"] || !record["Email"]) {
      this.toastService.showError('Required fields are missing');
      this.csvOrignalData = [];
      return false;
    }

    if (this.isPwcEnabled && (!record["PWC Email"] || (!record["Standard Pay Rate"] && !record["Foreign language Pay Rate"]) || !record["GUID"] || !record["Contractor Number"])) {
      this.toastService.showError('PWC Required fields are missing');
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private validateEmailFormats(record: any): boolean {
    if (!record['Email'].match(constants.emailRegex)) {
      this.toastService.showError('Invalid email');
      this.csvOrignalData = [];
      return false;
    }

    if (record['PWC Email'] && !record['PWC Email'].match(constants.emailRegex)) {
      this.toastService.showError('Invalid PWC email');
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private validateNumericFields(record: any): boolean {
    const numberColumns = ["Phone Number", "Default Rating"];
    const errorColumn = this.validateNumberColumns(record, numberColumns);
    
    if (errorColumn !== '') {
      this.toastService.showError(`${errorColumn} should be number`);
      this.csvOrignalData = [];
      return false;
    }

    if (this.isPwcEnabled) {
      return this.validatePwcNumericFields(record);
    }

    return true;
  }

  private validatePwcNumericFields(record: any): boolean {
    const pwcNumberColumns = ["Standard Pay Rate", "Foreign language Pay Rate"];
    const pwcErrorColumn = this.validateNumberColumns(record, pwcNumberColumns);
    
    if (pwcErrorColumn !== '') {
      this.toastService.showError(`${pwcErrorColumn} should be number`);
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private validateFieldRanges(record: any): boolean {
    return this.validatePhoneLength(record) &&
           this.validatePayRates(record) &&
           this.validateRating(record);
  }

  private validatePhoneLength(record: any): boolean {
    if (record["Phone Number"] && record["Phone Number"].toString().length > 10) {
      this.toastService.showError('One or more phone numbers is the incorrect length ( (10 digits)');
      this.csvOrignalData = [];
      return false;
    }
    return true;
  }

  private validatePayRates(record: any): boolean {
    if (!this.isPwcEnabled) return true;

    const primaryRate = Number(record["Standard Pay Rate"]);
    const secondaryRate = Number(record["Foreign language Pay Rate"]);

    if (record["Standard Pay Rate"] && (primaryRate > 1000 || primaryRate < 1)) {
      this.toastService.showError('Invalid standard rate');
      this.csvOrignalData = [];
      return false;
    }

    if (record["Foreign language Pay Rate"] && (secondaryRate > 1000 || secondaryRate < 1)) {
      this.toastService.showError('Invalid Foreign language rate');
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private validateRating(record: any): boolean {
    const rating = Number(record["Default Rating"]);
    if (record["Default Rating"] && (rating > 5 || rating < 0)) {
      this.toastService.showError('Default Rating should be less than 5 and greater than 0');
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private validateRanking(record: any): boolean {
    if (!this.isPwcEnabled) return true;

    if (record["Ranking"] && !Object.values(constants.userRankings).includes(record['Ranking'])) {
      this.toastService.showError(`Ranking should be ${Object.values(constants.userRankings).join(', ')}`);
      this.csvOrignalData = [];
      return false;
    }

    return true;
  }

  private processValidRecord(record: any): void {
    const jobTypes = this.getJobTypes(record);
    const existingUser = this.findExistingUser(record["Email"]);

    if (existingUser && jobTypes.length) {
      this.updateExistingUserJobTypes(record["Email"], jobTypes);
    } else if (!existingUser) {
      this.addNewUser(record, jobTypes);
    }
  }

  private findExistingUser(email: string): any {
    return this.csvOrignalData.find((data: any) => data.email === email);
  }

  private updateExistingUserJobTypes(email: string, jobTypes: string[]): void {
    this.csvOrignalData = this.csvOrignalData.map((csvData: any) => {
      if (email === csvData.email) {
        return { ...csvData, jobTypes };
      }
      return csvData;
    });
  }

  private addNewUser(record: any, jobTypes: string[]): void {
    const invitePayload = this.createInvitePayload(record, jobTypes);
    this.csvOrignalData.push(invitePayload);
  }

  private createInvitePayload(record: any, jobTypes: string[]): any {
    let invitePayload: any = {
      firstName: record["First Name"],
      lastName: record["Last Name"],
      email: record["Email"],
      phone: record["Phone Number"],
      rating: record["Default Rating"] || constants.defaultRatingValue,
    };

    if (!this.isSingleJobTypeEnabledFirm) {
      invitePayload.jobTypes = jobTypes;
    }

    if (this.isPwcEnabled) {
      invitePayload = this.addPwcFields(invitePayload, record);
    }

    return invitePayload;
  }

  private addPwcFields(invitePayload: any, record: any): any {
    return {
      ...invitePayload,
      pwcEmail: record["PWC Email"],
      primaryPayRate: Number(record["Standard Pay Rate"]).toFixed(2),
      secondaryPayRate: record["Foreign language Pay Rate"] && Number(record["Foreign language Pay Rate"]).toFixed(2),
      ranking: record["Ranking"],
      guid: record["GUID"],
      contractorNumber: record["Contractor Number"],
      secondaryContractorNumber: record["Secondary Contractor Number"]
    };
  }

  validateNumberColumns(row: any, cols: string[]){
    let errorColumn = '';
    for (const col of cols) {
      if (row[col] && Number.isNaN(Number(row[col]))) {
        errorColumn = col;
        break;
      }
    }
    return errorColumn;
  }

  getJobTypes(record: any){
    const jobTypes: string[] = [];
    if (!this.isSingleJobTypeEnabledFirm) {
      for (const jobHeader of this.jobTypeHeaders) {
        if (record[jobHeader]) {
          jobTypes.push(record[jobHeader]);
        }
      }        
    }
    return jobTypes;
  }

  getResult(lines: any, headers: any){
    const result = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) {
        continue;
      }
      result.push(this.getObj(headers, lines[i]));      
    }
    return result;
  }

  getObj(headers: any, line: any){
    const obj: any = {};
    const currentline = this.parseCSVLine(line);
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = currentline[j];
    }
    return obj;
  }

  parseCSVLine(line: string): string[] {
    const result = [];
    let currentField = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (insideQuotes && line[i + 1] === '"') {
          currentField += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        result.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }
    result.push(currentField.trim());

    return result;
  }

  readFile(file: File): void {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      if (reader.result) {
        this.csvData = this.parseCSV(reader.result as string);
        this.checkDuplication(this.csvData);
        this.csvPaginatedData = this.configureCsvPaginatedData();
      }
    };
  }

  checkDuplication(data: any[]): void {
    const emails = data.map(e => e.email);
    this.peopleService.checkEmailExistence(emails).subscribe(response => {
      if (response && response.data) {
        this.duplicateCsvUsers = response.data;
      }
    });
  }

  configureCsvPaginatedData(): any[] {
    const currentPage = this.pagination.currentPage - 1;
    const perPage = this.pagination.numPerPage;
    return this.csvOrignalData.slice(currentPage * perPage, this.pagination.currentPage * perPage);
  }

  downloadCSVTemplate(): void {
    const csv: string[] = [];
    const headers = this.expectedHeaders.join(',') + '\n';
    csv.push(headers);

    let users: any = [
      { firstName: 'Test1', lastName: 'User', email: 'test1.user@yopmail.com', phoneNo: '1312313123', rating: 3},
      { firstName: 'Test2', lastName: 'User', email: 'test2.user@yopmail.com', phoneNo: '1231212312', rating: 3}
    ];

    if (!this.isSingleJobTypeEnabledFirm) {
      users[0] =  { ...users[0], 'Job type 1': 'Job type value 1'};
      users[1] =  { ...users[1], 'Job type 1': 'Job type value 1'};
    }

    if(this.isPwcEnabled){
      users[0] =  { ...users[0], pwcEmail: 'test1.userpwc@yopmail.com', primaryPayRate: 15, secondaryPayRate: 10, ranking: 'QC', guid: 'GUID-001', contractorNumber: 'CN-001', secondaryContractorNumber: 'SCN-001'};
      users[1] =  { ...users[1], pwcEmail: 'test2.userpwc@yopmail.com', primaryPayRate: 15, secondaryPayRate: 10, ranking: 'QC', guid: 'GUID-002', contractorNumber: 'CN-002', secondaryContractorNumber: 'SCN-002'};
    }

    users.forEach((user: any) => {
      let cols: any = [
        user.firstName,
        user.lastName,
        user.email,
        user.phoneNo,
        user.rating
      ];

      if (!this.isSingleJobTypeEnabledFirm) {
        cols = [
          ...cols,
          user['Job type 1']
        ]
      }

      if(this.isPwcEnabled){
        cols = [
          ...cols,
          user.pwcEmail,
          user.primaryPayRate,
          user.secondaryPayRate,
          user.ranking,
          user.guid,
          user.contractorNumber,
          user.secondaryContractorNumber
        ]
      }

      cols = cols.join(',') + '\n';

      csv.push(cols);
    });

    const blob = new Blob(csv, { type: "text/plain;charset=utf-8" });
    saveAs(blob, "sampleBulkUpload.csv");
  }

  pageChanged(): void {
    this.csvPaginatedData = this.configureCsvPaginatedData();
  }

  checkIfExist(email: string): boolean {
    return this.duplicateCsvUsers.includes(email);
  }

  cancel(): void {
    this.ref.close();
  }

  fieldExistInCSV(field: string): boolean {
    return this.csvOrignalData.some(user => {
      const value = user[field];
      // Check if value exists and is not empty/whitespace
      return value != undefined && value != null && String(value).trim() !== '';
    });
  }
}
